import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
// CommandGroup is kept for the single flat group wrapper (cmdk requires
// items live inside a CommandGroup or CommandList to be filterable).
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type {
  AuditableUrl,
  AuditableUrlGroups,
} from "@/services/auditable-urls";

interface UrlComboboxProps {
  groups: AuditableUrlGroups;
  value: string;
  onChange: (item: AuditableUrl | null) => void;
  placeholder?: string;
  className?: string;
}

const flatten = (groups: AuditableUrlGroups): AuditableUrl[] =>
  Object.values(groups).flat();

export function UrlCombobox({
  groups,
  value,
  onChange,
  placeholder = "Choose a page to scan",
  className,
}: UrlComboboxProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => flatten(groups).find((item) => item.url === value) ?? null,
    [groups, value],
  );

  const triggerLabel = selected
    ? (selected.label?.trim() || selected.url)
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            "cy:bg-white cy:border cy:border-[#6c6c6c] cy:rounded-[4px] cy:px-[12px] cy:py-[10px]",
            "cy:flex cy:items-center cy:justify-between cy:gap-2",
            "cy:text-[13px] cy:text-left cy:w-full cy:max-w-[428px]",
            "cy:focus-visible:outline-none cy:focus-visible:ring-2 cy:focus-visible:ring-[#0b66e4]/30",
            className,
          )}
        >
          <span
            className={cn(
              "cy:truncate",
              selected ? "cy:text-[#21242d]" : "cy:text-[#767676]",
            )}
          >
            {triggerLabel}
          </span>
          <ChevronDown className="cy:size-5 cy:shrink-0 cy:text-[#21242d]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className={cn(
          "cy:w-[var(--radix-popover-trigger-width)] cy:max-w-[428px] cy:p-0",
          "cy:bg-white cy:border cy:border-[#d7d7d7] cy:rounded-[4px]",
          "cy:shadow-[0_6px_12px_rgba(23,0,121,0.1)]",
        )}
      >
        <Command
          filter={(value, search) =>
            value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          <CommandInput placeholder="Filter URLs…" className="cy:h-10" />
          <CommandList className="cy:max-h-[320px]">
            <CommandEmpty>No URLs match.</CommandEmpty>
            <CommandGroup>
              {Object.entries(groups).flatMap(([groupLabel, items]) =>
                items.map((item) => {
                  const title = item.label?.trim() ?? "";
                  const display = title ? `${title} — ${item.url}` : item.url;
                  return (
                    <CommandItem
                      key={`${groupLabel}::${item.url}`}
                      value={`${title} ${item.url}`}
                      onSelect={() => {
                        onChange(item);
                        setOpen(false);
                      }}
                    >
                      <span className="cy:text-[13px] cy:text-black cy:truncate cy:w-full">
                        {display}
                      </span>
                    </CommandItem>
                  );
                }),
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
